import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useRef, useState } from 'react';
import useCompanyUID from '../../hooks/useCompanyUID';
import { listShortlists, saveToShortlist, validShortlistName } from './shortlists';

export default function ShortlistDialog({ rows, searchContext, tr, onClose, onSaved, isRTL }) {
  useUiLanguage();
  const { companyUID, loading: companyLoading } = useCompanyUID();
  const dialog = useRef(null);
  const saving = useRef(false);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [target, setTarget] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { dialog.current?.showModal(); }, []);
  useEffect(() => {
    if (companyLoading) return undefined;
    let cancelled = false;
    setLoading(true); setLoadFailed(false); setError('');
    listShortlists(companyUID).then((values) => {
      if (cancelled) return;
      setLists(values); setTarget(values.length ? values[0].name : '');
    }).catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyUID, companyLoading, reload]);

  const name = target === '' ? newName.trim() : target;
  const save = async (event) => {
    event.preventDefault();
    if (saving.current || loading || companyLoading || !companyUID) return;
    if (!validShortlistName(name)) {
      setError(tr('Use a name up to 100 characters without . # $ / [ or ].', 'استخدم اسماً حتى 100 حرف دون . # $ / [ أو ].'));
      return;
    }
    saving.current = true; setBusy(true); setError('');
    try {
      const count = await saveToShortlist(companyUID, name, rows, searchContext);
      onSaved(name, count);
    } catch {
      setError(tr('Could not save the selection. Please try again.', 'تعذر حفظ المرشحين المحددين. حاول مجدداً.'));
    } finally { saving.current = false; setBusy(false); }
  };

  return <dialog ref={dialog} className="ta-shortlist-dialog" aria-labelledby="ta-shortlist-title" dir={isRTL ? 'rtl' : 'ltr'}
    onCancel={(event) => { event.preventDefault(); if (!saving.current) onClose(); }}>
    <form onSubmit={save}>
      <h2 id="ta-shortlist-title">{tr('Add to shortlist', 'أضف إلى قائمة مختصرة')}</h2>
      <p>{rows.length} {tr('selected', 'محدد')}</p>
      {loading || companyLoading ? <p role="status">{tr('Loading shortlists…', 'تحميل القوائم…')}</p> : loadFailed ?
        <p role="alert">{tr('Could not load your shortlists.', 'تعذر تحميل القوائم المختصرة.')} <button className="ta-link" type="button" onClick={() => setReload((value) => value + 1)}>{tr('Retry', 'إعادة المحاولة')}</button></p> : <>
          <label htmlFor="ta-shortlist-choice">{tr('Shortlist', 'القائمة المختصرة')}</label>
          <select id="ta-shortlist-choice" value={target} disabled={busy} onChange={(event) => setTarget(event.target.value)}>
            {lists.map((list) => <option key={list.name} value={list.name}>{list.name} ({list.count})</option>)}
            <option value="">{tr('Create new shortlist', 'إنشاء قائمة جديدة')}</option>
          </select>
          {target === '' && <><label htmlFor="ta-shortlist-name">{tr('Shortlist name', 'اسم القائمة')}</label>
            <input id="ta-shortlist-name" value={newName} maxLength={100} disabled={busy} onChange={(event) => setNewName(event.target.value)} placeholder={tr('e.g. Engineering shortlist', 'مثلاً: مرشحو الهندسة')} /></>}
        </>}
      {error && <p role="alert" className="ta-shortlist-error">{uiText(error)}</p>}
      <div className="ta-shortlist-actions">
        <button type="button" className="ta-outline" disabled={busy} onClick={onClose}>{tr('Cancel', 'إلغاء')}</button>
        <button className="ta-primary" disabled={busy || loading || companyLoading || loadFailed || !name}>{busy ? tr('Saving…', 'جارٍ الحفظ…') : tr('Save selection', 'حفظ المرشحين')}</button>
      </div>
    </form>
  </dialog>;
}
